import { CloudDownload, InfoCircle, Plus } from '@styled-icons/boxicons-regular';
import { PRODUCT_TYPE } from '@typedream/data';
import classNames from 'classnames';
import Link from 'next/link';
import { useState } from 'react';
import { useSpaceIDFromPath } from '../../data/space/hooks';
import ButtonAccent from '../../sharedComponents/Button/ButtonAccent';
import {
  useCheckPaypalConnectionQuery,
  useCheckStripeConnectionQuery,
  useCountProductsQuery,
  useGetSpaceQuery,
  useQueryProductsQuery,
} from '../../store';
import { EmptyProducts } from './EmptyProducts';
import { PopupCreateProduct } from './PopupCreateProduct';
import styles from './Products.module.css';
import sharedStyles from '../../styles/Shared.module.css';
import { ProductsContactEmail } from './ProductsContactEmail';
import { IPaginationProps, Pagination } from '../Pagination';
import Tooltip from '../../sharedComponents/TextEditor/components/Tooltip';

const defaultProductImage = {
  [PRODUCT_TYPE.DIGITAL_DOWNLOAD]: <CloudDownload size={35} className="with-accent-text-color" />,
};
const defaultProductDescription = {
  [PRODUCT_TYPE.DIGITAL_DOWNLOAD]: 'Downloadable digital files or product',
};

const productsPerPage = 5;

const initialPage: IPaginationProps = { offset: 0, size: productsPerPage };

export function Products() {
  const spaceID = useSpaceIDFromPath();
  const { data: space } = useGetSpaceQuery([spaceID]);
  const { data: totalItems } = useCountProductsQuery([spaceID]);
  const { data: stripeConnection } = useCheckStripeConnectionQuery([spaceID]);
  const { data: paypalConnection } = useCheckPaypalConnectionQuery([spaceID]);
  const isPaymentConfigured =
    stripeConnection?.status === 'configured' || paypalConnection?.status === 'configured';
  const [page, setpage] = useState<IPaginationProps>(initialPage);
  const [newProductOpen, setnewProductOpen] = useState(false);

  const { data: products } = useQueryProductsQuery([
    spaceID,
    {
      limit: productsPerPage,
      offset: page.offset,
    },
  ]);

  const onCreateCallback = async () => {
    setpage(initialPage);
  };

  return (
    <>
      <div className="is-flex is-align-items-center is-justify-content-space-between mb-4">
        <div className="is-size-5 with-text-color mb-1">Products</div>
        {products?.length > 0 && (
          <ButtonAccent onClick={() => setnewProductOpen(true)}>
            <Plus size={16} className="mr-2" />
            New Product
          </ButtonAccent>
        )}
      </div>
      {!products || products?.length <= 0 ? (
        <EmptyProducts createNewProduct={() => setnewProductOpen(true)} />
      ) : (
        <div>
          <div
            className="is-flex is-align-items-center is-justify-content-flex-end"
            style={{ padding: 10 }}
          >
            <div className="is-subtitle" style={{ width: 80 }}>
              Price
            </div>
            <div className="is-subtitle" style={{ width: 100 }}>
              Status
            </div>
          </div>
          {products?.map((prod) => {
            const product = prod.document;
            const productImage =
              product?.product_images?.length > 0 ? product?.product_images[0] : undefined;

            const productPrice = parseInt(product?.product_price, 10);
            return (
              <Link href={`/settings/${space?.slug}/${spaceID}/products/${prod.id}`} key={prod.id}>
                <div
                  className={classNames(
                    'card-border mb-4 with-cursor-pointer',
                    styles.productContainer
                  )}
                  key={prod.id}
                >
                  <div>
                    {productImage ? (
                      <div className={styles.productImageContainer}>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          className={styles.productImage}
                          src={productImage}
                          alt={product?.product_name}
                          width="65"
                          height="65"
                        />
                      </div>
                    ) : (
                      <div className={styles.productIconBackground}>
                        {defaultProductImage[product.product_type]}
                      </div>
                    )}
                  </div>

                  <div className="ml-5 is-flex-grow-1">
                    <div className={classNames(styles.ellipsis, 'with-text-color')}>
                      {product?.product_name}
                    </div>
                    <div className={classNames(styles.ellipsis, 'is-subtitle')}>
                      {product?.product_description ||
                        defaultProductDescription[product.product_type]}
                    </div>
                  </div>
                  {productPrice > 0 && !isPaymentConfigured && (
                    <Tooltip center="Connect Stripe Account to continue accepting payments">
                      <div className="tag is-danger is-light mx-4">
                        <InfoCircle size={13} className="mr-1" />
                        Payment Suspended
                      </div>
                    </Tooltip>
                  )}
                  <div className="is-subtitle" style={{ width: 80 }}>
                    ${productPrice}
                  </div>
                  <div style={{ width: 100 }}>
                    {prod.public ? (
                      <div className="tag is-primary is-light">Published</div>
                    ) : (
                      <div className="tag is-light">Draft</div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
          <Pagination totalItems={totalItems} page={page} setpage={setpage} />
        </div>
      )}

      <hr className={sharedStyles.hr} />
      <ProductsContactEmail />

      <PopupCreateProduct
        open={newProductOpen}
        setopen={setnewProductOpen}
        onCreateCallback={onCreateCallback}
      />
    </>
  );
}
